export default async (commit, query) => {
    const purchaseInfo = {};
    const userInfo = {};
    const cartInfo = {
        selectedAddons: [],
        selectedCharges: [],
        installments: {},
    };

    // Get the slug from the URL
    const url = new URL(window.location.href);
    const slug = url.pathname.split('/').pop();

    // Fetch the link data
    let response;
    try {
        response = await axios.get(`/api/v1/links/${slug}`);
    } catch (error) {
        window.Sentry.logError(error);
        console.log('Error fetching link data');
        commit('setLinkError', {
            active: true,
            code: error.response.status === 404 ? 'LinkNotFound' : 'GeneralError',
        });
    }

    if (!!response?.data?.plan) cartInfo.selectedPlanId = response?.data?.plan;
    if (!!response?.data?.billing_period) cartInfo.selectedBillingPeriod = `1 ${response?.data?.billing_period}`;
    if (!!response?.data?.billing_period) cartInfo.billingPeriod = response?.data?.billing_period;
    if (!!response?.data?.country) userInfo.country = response?.data?.country.toUpperCase();
    if (!!response?.data?.currency) userInfo.currency = response?.data?.currency.toUpperCase();

    // Set the IDs (Partner, Bundle, Event, Quote, Purchase, PostedBy)
    purchaseInfo.linkId = window.location.pathname.replace(/^\//, '');
    if (!!response?.data?.partner_id) purchaseInfo.partnerAccountId = response?.data?.partner_id;
    if (!!response?.data?.bundle_id) purchaseInfo.bundleId = response?.data?.bundle_id;
    if (!!response?.data?.event_id) purchaseInfo.eventId = response?.data?.event_id;
    if (!!response?.data?.quote_id) purchaseInfo.quoteId = response?.data?.quote_id;
    if (!!response?.data?.purchase_id) purchaseInfo.purchaseLinkId = response?.data?.purchase_id;
    if (!!response?.data?.posted_by_id) purchaseInfo.linkPostedBy = response?.data?.posted_by_id;
    // End of setting IDs

    // Set the source code and partner name
    if (!!response?.data?.source_code) purchaseInfo.sourceCode = response?.data?.source_code.toLowerCase();
    if (!!response?.data?.partner_name) purchaseInfo.partnerName = response?.data?.partner_name.toLowerCase();
    if (!!response?.data?.sales_channel) purchaseInfo.channel = response?.data?.sales_channel;
    // End of setting source code and partner name

    // Set boolean flags
    if (!!response?.data?.has_toggle) purchaseInfo.billingToggle = response?.data?.has_toggle || false;
    if (!!response?.data?.is_influencer) purchaseInfo.isInfluencer = response?.data?.is_influencer || false;
    if (!!response?.data?.has_etf) purchaseInfo.showEtf = response?.data?.has_etf || false;
    if (!!response?.data?.has_mbg90) purchaseInfo.showMbg90 = response?.data?.has_mbg90 || false;
    if (!!response?.data?.has_terms_conditions) purchaseInfo.showTc = response?.data?.has_terms_conditions || false;
    if (response?.data?.has_text_voice === false) {
        purchaseInfo.textVoiceTier = 0;
    }
    // End of setting boolean flags

    // Set the number of contacts and users
    if (!!response?.data?.total_contacts) purchaseInfo.contacts = response?.data?.total_contacts;
    if (!!response?.data?.total_users) purchaseInfo.users = response?.data?.total_users;
    // End of setting contacts and users

    // Set Chargebee data
    if (!!response?.data?.cb_customer_id) userInfo.customerId = response?.data?.cb_customer_id;
    if (!!response?.data?.cb_subscription_id) userInfo.subscriptionId = response?.data?.cb_subscription_id;
    // End of setting Chargebee data

    // Set coupons
    if (!!response?.data?.coupons) cartInfo.coupons = response?.data?.coupons.map(coupon => coupon.sku);
    // If no coupons are set, use the default coupon
    if (!cartInfo.coupons || cartInfo.coupons.length <= 0) {
        cartInfo.coupons = ['CURRENT'];
    }
    // End of setting coupons

    // Set installments
    if (!!response?.data?.installment_count) {
        if (response?.data?.installment_count > 1) {
            cartInfo.installments.count = response?.data?.installment_count;
            cartInfo.installments.enabled = true;

            // Add the installment charge
            cartInfo.selectedCharges.push({
                itemId: 'Installment-Charge',
                quantity: 1,
            });
        }
    }
    // End of setting installments

    // Prefill user data if set
    if (!!response?.data?.email) userInfo.email = response?.data?.email;
    if (!!response?.data?.first_name) userInfo.firstName = response?.data?.first_name;
    if (!!response?.data?.last_name) userInfo.lastName = response?.data?.last_name;
    if (!!response?.data?.phone) userInfo.phone = response?.data?.phone;
    if (!!response?.data?.company) userInfo.company = response?.data?.company;
    // End of prefill

    // Thryv Cross Sell
    if (!!response?.data?.thryv_eaid) {
        purchaseInfo.thryvCrossSell = {
            ...purchaseInfo.thryvCrossSell,
            eaid: response?.data?.thryv_eaid,
        };
    }
    if (!!response?.data?.thryv_sales_rep) {
        purchaseInfo.thryvCrossSell = {
            ...purchaseInfo.thryvCrossSell,
            salesRep: response?.data?.thryv_sales_rep,
        };
    };
    if (!!response?.data?.thryv_business_advisor) {
        purchaseInfo.thryvCrossSell = {
            ...purchaseInfo.thryvCrossSell,
            businessAdvisorId: response?.data?.thryv_business_advisor,
        };
    };
    // End of Thryv Cross Sell

    // Check if the user is upgrading or adding a subscription
    if (
        !!userInfo.subscriptionId
        && (
            !!purchaseInfo.partnerAccountId
            || !!purchaseInfo.purchaseLinkId
        )
    ) {
        commit('setIsUpgrading', true);
    } else if (
        !userInfo.subscriptionId
        && !!userInfo.customerId
        && (
            !!purchaseInfo.partnerAccountId
            || !!purchaseInfo.purchaseLinkId
        )
    ) {
        commit('setIsAddingSub', true);
    }
    // End of check

    // Load charges into cartInfo obj
    if (!!response?.data?.charges && typeof response?.data?.charges === 'object' && response?.data?.charges.length > 0) {
        const charges = response?.data?.charges;
        charges.forEach(charge => {
            cartInfo.selectedCharges.push({
                itemId: charge.sku,
                quantity: charge.quantity,
            });
        });
    }
    // Load addons into cartInfo obj
    if (!!response?.data?.addons && typeof response?.data?.addons === 'object' && response?.data?.addons.length > 0) {
        const addons = response?.data?.addons;
        addons.forEach(addon => {
            cartInfo.selectedAddons.push({
                itemId: addon.sku,
                quantity: addon.quantity,
            });
        });
    }
    // Dedupe charges
    if (cartInfo.selectedCharges.length > 0) {
        const uniqueCharges = [];
        const chargeIds = [];
        cartInfo.selectedCharges.forEach(charge => {
            if (!chargeIds.includes(charge.itemId)) {
                chargeIds.push(charge.itemId);
                uniqueCharges.push(charge);
            }
        });
        cartInfo.selectedCharges = uniqueCharges;
    }
    // Dedupe addons
    if (cartInfo.selectedAddons.length > 0) {
        const uniqueAddons = [];
        const addonIds = [];
        cartInfo.selectedAddons.forEach(addon => {
            if (!addonIds.includes(addon.itemId)) {
                addonIds.push(addon.itemId);
                uniqueAddons.push(addon);
            }
        });
        // Filter out the included addons and contacts
        cartInfo.selectedAddons = uniqueAddons.filter(addon => {
            return !addon.itemId.includes('included-contact') && !addon.itemId.includes('included-user');
        });
    }

    // Commit everything to state
    if (Object.keys(purchaseInfo).length > 0) {
        await commit('updatePurchaseInfo', purchaseInfo);
    }
    if (Object.keys(userInfo).length > 0) {
        await commit('updateUserInfo', userInfo);
    }
    if (Object.keys(cartInfo).length > 0) {
        await commit('updateCartInfo', cartInfo);
    }
}
